import { EyeOpenIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  IBulkImportSummary,
  IHospitalDetail,
} from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import { TableSorted } from "../../../../components/TableSorted";
import {
  BULK_IMPORTS_QUERY_KEY,
  useBulkImports,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { RowActions } from "../../Patients/AdminPatients.styles";
import {
  ErrorMessage,
  FieldHint,
  FilterButton,
  HeaderActions,
  RowAction,
  PageInfoTooltip,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../../shared";
import { BulkImportJobModal } from "./BulkImportJobModal";
import { TabStack } from "./HospitalDetails.styles";
import { StartBulkImportModal } from "./StartBulkImportModal";

/**
 * Badge tone for a job row. Any failed row makes the job worth a second look
 * even when its status is "completed" — a partially-successful import is the
 * case an operator most needs to notice.
 */
function jobTone(job: IBulkImportSummary): "active" | "warning" | "inactive" {
  if (job.failedRows > 0) return "warning";
  return job.status?.toLowerCase() === "completed" ? "active" : "inactive";
}

interface IHospitalBulkImportTabProps {
  hospital: IHospitalDetail;
  wards: IWard[];
}

/**
 * Bulk import tab of the Hospital Details page (EN-5175) — Retool's
 * `modulePatientBulkImport`.
 *
 * ⚠️ **Creates patient records in bulk.** The upload is behind a confirmation
 * that names the target ward and requires an explicit tick; see
 * `StartBulkImportModal`.
 *
 * ⚠️ **The job list is NOT hospital-scoped.** `GET /Retool/Patients/BulkImports`
 * returns every import in the region — it has no hospital filter — so a job
 * belonging to another hospital appears here. The ward column is what tells
 * them apart, and jobs whose ward is not one of this hospital's are labelled as
 * such rather than silently implying they were run from this page. Retool had
 * the same list and did not say so.
 */
export function HospitalBulkImportTab({
  hospital,
  wards,
}: IHospitalBulkImportTabProps) {
  const { openModal, closeModal } = useModal();
  const { bulkImports, isLoading, isError } = useBulkImports();

  const wardNamesById = useMemo(
    () => new Map(wards.map((ward) => [Number(ward.id), ward.name])),
    [wards]
  );

  const openJob = (jobId: string, summary?: IBulkImportSummary) =>
    openModal(
      <BulkImportJobModal
        jobId={jobId}
        summary={summary}
        wardName={
          summary ? wardNamesById.get(summary.wardId) ?? undefined : undefined
        }
      />,
      { width: "min(760px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );

  const { items, allItems, pagination, setPage, setPageSize } =
    useMockPagination({
      data: bulkImports,
      searchFields: ["status"],
    });

  const columns: ColumnDef<IBulkImportSummary, string>[] = useMemo(
    () => [
      {
        accessorKey: "created",
        header: "Queued",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        // The list spans the region, so this is what identifies whose job it is.
        id: "ward",
        header: "Ward",
        accessorFn: (row) =>
          wardNamesById.get(row.wardId) ??
          `Ward ${row.wardId} (another hospital)`,
        enableSorting: true,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => row.status || "-",
        cell: ({ row }) => (
          <StatusBadge status={jobTone(row.original)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "totalRows",
        header: "Rows",
        accessorFn: (row) => String(row.totalRows ?? 0),
        enableSorting: true,
      },
      {
        id: "succeededRows",
        header: "Created",
        accessorFn: (row) => String(row.succeededRows ?? 0),
        enableSorting: true,
      },
      {
        id: "failedRows",
        header: "Failed",
        accessorFn: (row) => String(row.failedRows ?? 0),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="View result"
              icon={<EyeOpenIcon />}
              onClick={() => {
                openJob(row.original.jobId, row.original);
              }}
            />
          </RowActions>
        ),
      },
    ],
    [wardNamesById]
  );

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Bulk import</SectionTitle>
            <PageInfoTooltip title="Bulk import">
              Create many patients from a CSV, one ward at a time
            </PageInfoTooltip>
          </SectionTitleGroup>
          <HeaderActions>
            <FilterButton
              variant="primary"
              disabled={wards.length === 0}
              onClick={() =>
                openModal(
                  <StartBulkImportModal
                    hospital={hospital}
                    wards={wards}
                    onQueued={(jobId) => {
                      closeModal();
                      // Straight to the result, which polls itself — the
                      // operator's next question is always "did it work".
                      openJob(jobId);
                    }}
                    onCancel={closeModal}
                  />,
                  {
                    // Wide enough for the CSV contract table inside the form.
                    width: "min(880px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                )
              }
            >
              Import patients
            </FilterButton>
          </HeaderActions>
        </SectionHeader>

        {wards.length === 0 && (
          <FieldHint>
            This hospital has no wards yet, so there is nowhere to import into.
          </FieldHint>
        )}

        <FieldHint>
          This list covers every import in the region, not only this
          hospital&apos;s — the endpoint has no hospital filter. The ward column
          says which is which.
        </FieldHint>

        {isError && (
          <ErrorMessage>
            Failed to load the import jobs. Try refreshing the page.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[[BULK_IMPORTS_QUERY_KEY]]}>
            <TableSorted
              columns={columns}
              data={items}
              emptyStateText={
                isLoading ? <TableLoadingState /> : "No imports have been run"
              }
              onRowClick={(row) => openJob(row.jobId, row)}
            />
          </TableWrapper>
          <TablePagination
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            exportCsv={{
              filename: "bulk-imports",
              columns,
              rows: allItems,
            }}
          />
        </TableContainer>
      </SectionCard>
    </TabStack>
  );
}
